// src/hooks/useCurrentSales.ts
import { useGetIdentity , useGetList } from "ra-core";
import type { Sale } from "../types";

/**
 * Resolve the current authenticated user to their `sales` record.
 *
 * `identity.id` is the Supabase auth uuid. `sales.id` is a bigint. This hook
 * bridges them via `sales.auth_user_id = identity.id`.
 *
 * Returns undefined while loading or if there's no matching sales row.
 */
export function useCurrentSales(): (Sale & { id: number }) | undefined {
  const { identity, isPending: isIdentityPending } = useGetIdentity();

  const { data } = useGetList<Sale>(
    "sales",
    {
      pagination: { page: 1, perPage: 1 },
      filter: identity?.id ? { auth_user_id: identity.id } : {},
    },
    { enabled: !isIdentityPending && !!identity?.id },
  );

  if (isIdentityPending || !identity?.id) return undefined;
  const rows = Array.isArray(data) ? data : (data?.data ?? []);
  return rows[0] as (Sale & { id: number }) | undefined;
}

/** Convenience: just the bigint sales id. */
export function useCurrentSalesId(): number | undefined {
  const { identity } = useGetIdentity();
  if (identity?.id == null) return undefined;
  const n = Number(identity.id);
  return Number.isNaN(n) ? undefined : n;
}